import ky from "ky";
import pLimit from "p-limit";
import ENDPOINTS from "../data/endpoints.json";
import isEmpty from "lodash/isEmpty";

const httpConcurrencyLimit = pLimit(5);

const limitedFetch = (...args) => {
  return httpConcurrencyLimit(() => fetch(...args));
};

const isMissingOrInvalidPayload = (payload) => {
  if (!payload) return false;

  const message =
    typeof payload === "string"
      ? payload
      : payload.message || payload.error || payload.errorMessage || payload.msg;

  return (
    typeof message === "string" &&
    message.trim().toLowerCase() === "missing or invalid payload"
  );
};

const hasSignatureExpired = (payload) => {
  if (!payload) return false;

  const message =
    typeof payload === "string"
      ? payload
      : payload.message || payload.error || payload.errorMessage || payload.msg;

  return (
    typeof message === "string" &&
    message.trim().toLowerCase() === "signature has expired"
  );
};

const redirectToLogin = () => {
  const targetHash = "#/";
  if (window.location.hash === targetHash) {
    return;
  }

  if (window.location.hash) {
    window.location.hash = targetHash;
    return;
  }

  window.location.href = `${window.location.origin}${window.location.pathname}${targetHash}`;
};

const decodeJwtPayload = (jwt) => {
  if (!jwt || typeof jwt !== "string") return null;

  const segments = jwt.split(".");
  if (segments.length < 2) return null;

  try {
    const base64Url = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64Url.padEnd(
      base64Url.length + ((4 - (base64Url.length % 4)) % 4),
      "=",
    );
    const binary = atob(padded);
    const json = decodeURIComponent(
      Array.from(binary)
        .map((char) => {
          return `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join(""),
    );

    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const getJwtExpiry = (jwt) => {
  const payload = decodeJwtPayload(jwt);
  if (!payload || payload.exp == null) return null;

  return Number(payload.exp) * 1000;
};

export const isJwtExpired = (jwt) => {
  const expMs = getJwtExpiry(jwt);
  if (expMs == null) return false;

  return Date.now() >= expMs;
};

export const isJwtExpiringSoon = (jwt, secondsAhead = 120) => {
  const expMs = getJwtExpiry(jwt);
  if (expMs == null) return false;

  return Date.now() + secondsAhead * 1000 >= expMs;
};

const getCurrentSchoolFromStorage = () => {
  try {
    const storedSchool = window.localStorage.getItem("Auth.school");
    if (!storedSchool) return null;
    return JSON.parse(storedSchool);
  } catch {
    return null;
  }
};

export const refreshJwtIfNeeded = async (jwt) => {
  if (!jwt || !isJwtExpiringSoon(jwt)) {
    return jwt;
  }

  try {
    const school = getCurrentSchoolFromStorage();
    if (!school || !school.region) {
      return null;
    }

    const url = `${ENDPOINTS[school.region].user}/user/jwt/refresh`;
    const response = await ky
      .post(url, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      })
      .json();

    const refreshedJwt =
      response?.jwt ||
      response?.token ||
      response?.accessToken ||
      response?.data?.jwt ||
      response?.data?.token ||
      response?.result?.jwt ||
      response?.result?.token ||
      null;

    return refreshedJwt;
  } catch {
    return null;
  }
};

export const ensureJwtUsable = async (jwt, onRefreshedJwt = null) => {
  if (!jwt || jwt == null) return jwt;

  if (isJwtExpired(jwt)) {
    redirectToLogin();
    return null;
  }

  if (isJwtExpiringSoon(jwt)) {
    const refreshedJwt = await refreshJwtIfNeeded(jwt);
    if (!refreshedJwt) {
      redirectToLogin();
      return null;
    }

    if (typeof onRefreshedJwt === "function") {
      onRefreshedJwt(refreshedJwt);
    }

    return refreshedJwt;
  }

  return jwt;
};

const handleApiResponse = async (request) => {
  try {
    const payload = await request;

    if (isMissingOrInvalidPayload(payload) || hasSignatureExpired(payload)) {
      redirectToLogin();
    }

    return payload;
  } catch (error) {
    let payload = null;

    try {
      const response = error?.response;
      if (response) {
        const text = await response.clone().text();
        if (text) {
          payload = JSON.parse(text);
        }
      }
    } catch {
      payload = null;
    }

    if (isMissingOrInvalidPayload(payload) || hasSignatureExpired(payload)) {
      redirectToLogin();
    }

    throw error;
  }
};

export const generateUUID = () => {
  // Public Domain/MIT
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== "undefined" &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    let r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = ((d + r) % 16) | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = ((d2 + r) % 16) | 0;
      d2 = Math.floor(d2 / 16);
    }
    // eslint-disable-next-line no-mixed-operators
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const buildHeaders = (jwt = null, extraHeaders = null) => {
  let headers = {
    Accept: "application/json",
  };
  if (jwt != null) {
    headers.Authorization = `Bearer ${jwt}`;
  }
  // merge them
  if (extraHeaders != null) {
    headers = { ...headers, ...extraHeaders };
  }
  return headers;
};

export const getKy = (jwt, extraHeaders = null) => {
  let retryLimit = 5;
  if (process.env.NODE_ENV !== "production") {
    retryLimit = 1;
  }

  const safeJwt = jwt;
  if (safeJwt && safeJwt != null && isJwtExpired(safeJwt)) {
    redirectToLogin();
  }


  const options = {
    retry: {
      limit: retryLimit,
      methods: ["get", "post", "put", "delete"],
      statusCodes: [408, 429, 500, 502, 503, 504],
      afterStatusCodes: [429, 503],
      jitter: true,
      delay: (attemptCount) => 500 * 2 ** (attemptCount - 1),
    },
    timeout: 30_000,
    fetch: limitedFetch,
  };

  options.headers = buildHeaders(safeJwt, extraHeaders);

  const api = ky.create(options);
  return api;
};

export const httpPut = async (jwt, url, values, onRefreshedJwt = null) => {
  const activeJwt = await ensureJwtUsable(jwt, onRefreshedJwt);
  if (jwt != null && !activeJwt) return null;

  return handleApiResponse(
    getKy(activeJwt)
      .put(url, {
        json: values,
      })
      .json(),
  );
};

export const httpPost = async (jwt, url, values, onRefreshedJwt = null) => {
  let activeJwt = null;

  if (!isEmpty(jwt)) {
    activeJwt = await ensureJwtUsable(jwt, onRefreshedJwt);
    if (jwt != null && !activeJwt) return null;
  }

  return handleApiResponse(
    getKy(activeJwt)
      .post(url, {
        json: values,
      })
      .json(),
  );
};

/**
 * used for s3 puts
 * @param {*} signedUrl
 * @param {*} fileObj
 * @param {*} contentType
 * @returns
 */
export const httpUpload = (signedUrl, fileObj, contentType) => {
  return getKy(null).put(signedUrl, {
    body: fileObj,
    headers: {
      "Content-Type": contentType,
    },
  });
};

export const httpGet = async (jwt, url, onRefreshedJwt = null) => {
  const activeJwt = await ensureJwtUsable(jwt, onRefreshedJwt);
  if (jwt != null && !activeJwt) return null;

  return handleApiResponse(getKy(activeJwt).get(url).json());
};

export const httpDelete = async (jwt, url, onRefreshedJwt = null) => {
  const activeJwt = await ensureJwtUsable(jwt, onRefreshedJwt);
  if (jwt != null && !activeJwt) return null;

  return handleApiResponse(getKy(activeJwt).delete(url).json());
};

export const getHeartbeatStatus = (url) => {
  const options = {
    retry: {
      limit: 0,
    },
    timeout: 15_000,
    fetch: limitedFetch,
  };

  options.headers = buildHeaders(null, null);
  const api = ky.create(options);
  return api.get(url).json();
};
